// src/components/item-builder/CreateMcqMatrixModal.jsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import { Editor } from "@tinymce/tinymce-react";
import { Plus, Trash2 } from "lucide-react";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";

const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  rows: [
    { key: "r1", fa: "", en: "", correctCount: 1 }, // تعداد جواب صحیح برای این ردیف
    { key: "r2", fa: "", en: "", correctCount: 1 },
  ],
  cols: [
    { key: "A", fa: "", en: "" },
    { key: "B", fa: "", en: "" },
  ],
  correct: {},
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";

  const rows = Array.isArray(item?.body?.rows)
    ? item.body.rows.map((r) => ({
        key: r.key,
        fa: r?.label?.fa || "",
        en: r?.label?.en || "",
      }))
    : makeEmptyForm().rows;

  const cols = Array.isArray(item?.body?.cols)
    ? item.body.cols.map((c) => ({
        key: c.key,
        fa: c?.label?.fa || "",
        en: c?.label?.en || "",
      }))
    : makeEmptyForm().cols;

  const scoring = item.scoring || item.body?.scoring || {};
  const answerMap = scoring.answerMap || {};

  const correct = {};
  Object.entries(answerMap).forEach(([rk, raw]) => {
    if (Array.isArray(raw)) correct[rk] = raw;
    else if (raw != null) correct[rk] = [raw];
  });

  const points = item?.points ?? 1;
  const status = item?.status || "published";
  const tags = Array.isArray(item?.tags) ? item.tags.join(",") : "";
  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa,
    stem_en,
    rows,
    cols,
    correct,
    points,
    status,
    tags,
    explanation_fa,
    explanation_en,
  };
};

export default function McqMatrixModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(() =>
    item ? itemToForm(item) : makeEmptyForm(),
  );

  useEffect(() => {
    if (!isOpen) return;
    if (item) {
      setForm(itemToForm(item));
    } else {
      setForm(makeEmptyForm());
    }
  }, [isOpen, item]);

  if (!isOpen) return null;

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  // ----- Rows -----
  const addRow = () => {
    const nextIndex = form.rows.length + 1;
    setForm((f) => ({
      ...f,
      rows: [...f.rows, { key: `r${nextIndex}`, fa: "", en: "" }],
    }));
  };

  const removeRow = (idx) => {
    if (form.rows.length <= 1) return;
    const removed = form.rows[idx];
    const nextRows = form.rows.filter((_, i) => i !== idx);
    const nextCorrect = { ...form.correct };
    delete nextCorrect[removed.key];

    setForm((f) => ({
      ...f,
      rows: nextRows,
      correct: nextCorrect,
    }));
  };

  // ----- Columns -----
  const addCol = () => {
    const nextKey = String.fromCharCode(65 + form.cols.length); // A,B,C,...
    setForm((f) => ({
      ...f,
      cols: [...f.cols, { key: nextKey, fa: "", en: "" }],
    }));
  };

  const removeCol = (idx) => {
    if (form.cols.length <= 2) return;
    const removed = form.cols[idx];
    const nextCols = form.cols.filter((_, i) => i !== idx);

    const nextCorrect = {};
    Object.entries(form.correct).forEach(([rowKey, arr]) => {
      const list = Array.isArray(arr) ? arr : arr ? [arr] : [];
      const filtered = list.filter((k) => k !== removed.key);
      if (filtered.length) nextCorrect[rowKey] = filtered;
    });

    setForm((f) => ({
      ...f,
      cols: nextCols,
      correct: nextCorrect,
    }));
  };

  // ----- Correct answers per row (multi) -----
  const toggleCorrect = (rowKey, colKey) => {
    setForm((f) => {
      const prev = Array.isArray(f.correct[rowKey]) ? f.correct[rowKey] : [];
      const row = f.rows.find((r) => r.key === rowKey); // پیدا کردن ردیف
      const maxCorrect = row ? row.correctCount : 1; // تعداد جواب‌های صحیح مجاز برای این ردیف
      const has = prev.includes(colKey);
      const nextForRow = has
        ? prev.filter((k) => k !== colKey)
        : [...prev, colKey];

      // بررسی تعداد پاسخ صحیح بیشتر از حد مجاز
      if (nextForRow.length <= maxCorrect) {
        return {
          ...f,
          correct: {
            ...f.correct,
            [rowKey]: nextForRow,
          },
        };
      }

      return f; // اگر بیش از حد مجاز باشد هیچ تغییری ایجاد نکن
    });
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!form.stem_fa.trim() && !form.stem_en.trim()) {
      toast.error(t("mcq_matrix.toasts.stem_required", "متن سؤال لازم است"));
      return;
    }

    const cleanRows = form.rows.filter((r) =>
      (r.fa || r.en)?.toString().trim(),
    );
    const cleanCols = form.cols.filter((c) =>
      (c.fa || c.en)?.toString().trim(),
    );

    if (cleanRows.length < 1) {
      toast.error(t("mcq_matrix.toasts.min_rows", "حداقل یک ردیف لازم است"));
      return;
    }
    if (cleanCols.length < 2) {
      toast.error(t("mcq_matrix.toasts.min_cols", "حداقل دو ستون لازم است"));
      return;
    }

    const missing = cleanRows.filter((r) => {
      const raw = form.correct[r.key];
      const arr = Array.isArray(raw) ? raw : raw ? [raw] : [];
      return arr.length === 0;
    });
    if (missing.length) {
      toast.error(
        t(
          "mcq_matrix.toasts.missing_correct",
          "برای تمام ردیف‌ها حداقل یک گزینهٔ صحیح تعیین کنید",
        ),
      );
      return;
    }

    const answerMap = {};
    cleanRows.forEach((r) => {
      const raw = form.correct[r.key];
      const arr = Array.isArray(raw) ? raw : raw ? [raw] : [];
      answerMap[r.key] = arr.filter(Boolean);
    });

    const payload = {
      kind: "mcq_matrix",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: {
        fa: form.stem_fa || undefined,
        en: form.stem_en || undefined,
      },
      body: {
        rows: cleanRows.map((r) => ({
          key: r.key,
          label: {
            fa: r.fa || undefined,
            en: r.en || undefined,
          },
          correctCount: r.correctCount, // تعداد جواب‌های صحیح برای هر ردیف
        })),
        cols: cleanCols.map((c) => ({
          key: c.key,
          label: {
            fa: c.fa || undefined,
            en: c.en || undefined,
          },
        })),
      },
      scoring: {
        mode: "matrix_exact",
        answerMap,
      },
      points: Number(form.points) || 1,
      status: form.status || "published",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };
    
    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t("mcq_matrix.toasts.updated", "سؤال ماتریسی با موفقیت ویرایش شد")
          : t("mcq_matrix.toasts.created", "سؤال ماتریسی با موفقیت ساخته شد"),
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  return (
    <form onSubmit={submit} className="mb-6 space-y-4">
      {/* Stem */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div dir={isRtl ? "rtl" : "ltr"}>
          <label className="block text-sm font-medium mb-1">
            {t("mcq_matrix.stem_fa", "متن سؤال (فارسی)")}
          </label>
          <CustomEditor
            value={form.stem_fa}
            onEditorChange={(content) => handleEditorChange("stem_fa", content)}
            placeholder={t("mcq_matrix.stem_fa_ph", "صورت سؤال (فارسی)")}
          />
        </div>
        <div dir="ltr">
          <label className="block text-sm font-medium mb-1">
            {t("mcq_matrix.stem_en", "متن سؤال (انگلیسی)")}
          </label>
          <CustomEditor
            value={form.stem_en}
            onEditorChange={(content) => handleEditorChange("stem_en", content)}
            placeholder={t("mcq_matrix.stem_en_ph", "Question text (English)")}
          />
        </div>
      </div>

      {/* Rows config */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium">
            {t("mcq_matrix.rows", "ردیف‌ها")}
          </label>
          <button
            type="button"
            onClick={addRow}
            className="text-blue-600 text-sm inline-flex items-center gap-1"
          >
            <Plus size={16} />
            {t("mcq_matrix.add_row", "افزودن ردیف")}
          </button>
        </div>
        <div className="space-y-2">
          {form.rows.map((row, idx) => (
            <div
              key={row.key}
              className="border rounded-lg p-3 flex flex-col gap-2"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold">
                  {t("mcq_matrix.row_n", { n: row.key })}
                </span>
                {form.rows.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    className="p-1 rounded-full text-red-600 hover:bg-red-100"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  className="border rounded-lg px-3 py-2 text-sm"
                  placeholder={t("mcq_matrix.row_fa_ph", "متن ردیف (فارسی)")}
                  value={row.fa}
                  onChange={(e) => {
                    const next = [...form.rows];
                    next[idx].fa = e.target.value;
                    setForm((f) => ({ ...f, rows: next }));
                  }}
                />
                <input
                  className="border rounded-lg px-3 py-2 text-sm"
                  placeholder={t("mcq_matrix.row_en_ph", "Row text (English)")}
                  value={row.en}
                  onChange={(e) => {
                    const next = [...form.rows];
                    next[idx].en = e.target.value;
                    setForm((f) => ({ ...f, rows: next }));
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Columns config */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium">
            {t("mcq_matrix.cols", "ستون‌ها / گزینه‌ها")}
          </label>
          <button
            type="button"
            onClick={addCol}
            className="text-blue-600 text-sm inline-flex items-center gap-1"
          >
            <Plus size={16} />
            {t("mcq_matrix.add_col", "افزودن ستون")}
          </button>
        </div>
        <div className="space-y-2">
          {form.cols.map((col, idx) => (
            <div
              key={col.key}
              className="border rounded-lg p-3 flex flex-col gap-2"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold">
                  {t("mcq_matrix.col_n", { n: col.key })}
                </span>
                {form.cols.length > 2 && (
                  <button
                    type="button"
                    onClick={() => removeCol(idx)}
                    className="p-1 rounded-full text-red-600 hover:bg-red-100"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  className="border rounded-lg px-3 py-2 text-sm"
                  placeholder={t("mcq_matrix.col_fa_ph", "متن ستون (فارسی)")}
                  value={col.fa}
                  onChange={(e) => {
                    const next = [...form.cols];
                    next[idx].fa = e.target.value;
                    setForm((f) => ({ ...f, cols: next }));
                  }}
                />
                <input
                  className="border rounded-lg px-3 py-2 text-sm"
                  placeholder={t(
                    "mcq_matrix.col_en_ph",
                    "Column text (English)",
                  )}
                  value={col.en}
                  onChange={(e) => {
                    const next = [...form.cols];
                    next[idx].en = e.target.value;
                    setForm((f) => ({ ...f, cols: next }));
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {form.rows.map((row) => (
          <div key={row.key} className="border rounded-lg p-3">
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium">{`تعداد جواب‌های صحیح برای ${row.fa || row.en || row.key}`}</label>
              <input
                type="number"
                value={row.correctCount}
                min={1}
                onChange={(e) => {
                  const updatedRows = form.rows.map((r) =>
                    r.key === row.key
                      ? { ...r, correctCount: parseInt(e.target.value) }
                      : r,
                  );
                  setForm({ ...form, rows: updatedRows });
                }}
                className="w-16 p-1 border rounded-md text-center"
              />
            </div>
          </div>
        ))}
      </div>

      {/* ✅ Correct answers per row */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {t("mcq_matrix.correct_map", "جواب‌های صحیح هر ردیف")}
        </label>
        <div className="space-y-3">
          {form.rows.map((row) => {
            const rowLabel = row.fa || row.en || row.key;
            const selected = Array.isArray(form.correct[row.key])
              ? form.correct[row.key]
              : [];
            return (
              <div key={row.key} className="border rounded-lg p-3">
                <div className="text-sm font-medium mb-2">{rowLabel}</div>
                <div className="flex flex-wrap gap-3">
                  {form.cols.map((col) => {
                    const colLabel = col.fa || col.en || col.key;
                    const checked = selected.includes(col.key);
                    return (
                      <label
                        key={col.key}
                        className="inline-flex items-center gap-1 text-xs border rounded-full px-3 py-1 cursor-pointer hover:bg-purple-50"
                      >
                        <input
                          type="checkbox"
                          className="h-3 w-3 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          checked={checked}
                          onChange={() => toggleCorrect(row.key, col.key)}
                        />
                        <span>
                          {col.key} - {colLabel}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Meta */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("mcq_matrix.points", "امتیاز")}
          </label>
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={form.points}
            onChange={(e) => setForm((f) => ({ ...f, points: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("mcq_matrix.status", "وضعیت")}
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="published">
              {t("common.published", "منتشر شده")}
            </option>
            <option value="draft">{t("common.draft", "پیش‌نویس")}</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("mcq_matrix.tags", "تگ‌ها")}
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2 text-sm"
            placeholder={t("common.tags_csv", "مثال: grammar,unit1")}
            value={form.tags}
            onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
          />
        </div>
      </div>

      <ExplanationField
        valueFa={form.explanation_fa}
        valueEn={form.explanation_en}
        onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
        onChangeEn={(v) => handleEditorChange("explanation_en", v)}
        isRtl={isRtl}
      />

      <div className="flex justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm rounded-lg bg-gray-200 text-gray-700"
        >
          {t("common.cancel", "انصراف")}
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white"
        >
          {t("common.create", "ایجاد")}
        </button>
      </div>
    </form>
  );
}
